import { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Textarea,
  FormHelperText,
  FormErrorMessage,
  Text,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { useActivityResponse } from '../../hooks/useActivityResponse';
import type { ActivityComponentProps } from '../../types/learningActivities';

interface TextQuestionProps extends ActivityComponentProps {
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}

export function TextQuestion({
  activityKey,
  required = false,
  placeholder,
  rows = 4,
  maxLength,
  onResponseChange,
}: TextQuestionProps) {
  const { activity, response, isLoading, error, saveResponse, isSaving } = useActivityResponse(activityKey);
  const [value, setValue] = useState('');
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Initialize value from existing response
  useEffect(() => {
    if (response?.response_value) {
      setValue(response.response_value);
    }
  }, [response]);

  // Auto-save with debounce
  const handleChange = (newValue: string) => {
    setValue(newValue);

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout to save after 1 second of no typing
    const timeout = setTimeout(async () => {
      try {
        await saveResponse(newValue || null);
        onResponseChange?.(newValue || null);
      } catch (err) {
        console.error('Failed to save response:', err);
      }
    }, 1000);

    setSaveTimeout(timeout);
  };

  if (isLoading) {
    return (
      <Box py={2}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error || !activity) {
    return (
      <Text color="red.500" fontSize="sm">
        {error || 'Failed to load activity'}
      </Text>
    );
  }

  const config = activity.config || {};
  const finalPlaceholder = placeholder || config.placeholder || 'Type your answer here...';
  const finalRows = rows || config.rows || 4;
  const finalMaxLength = maxLength || config.max_length;

  return (
    <FormControl isRequired={required} isInvalid={required && !value.trim()}>
      <FormLabel fontWeight="semibold">
        {activity.prompt}
        {required && <Text as="span" color="red.500" ml={1}>*</Text>}
      </FormLabel>
      
      <Textarea
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={finalPlaceholder}
        rows={finalRows}
        maxLength={finalMaxLength}
        resize="vertical"
        autoComplete="off"
        spellCheck="true"
      />
      
      {config.help_text && (
        <FormHelperText>{config.help_text}</FormHelperText>
      )}
      
      {finalMaxLength && (
        <FormHelperText textAlign="right" fontSize="xs">
          {value.length} / {finalMaxLength}
        </FormHelperText>
      )}
      
      {isSaving && (
        <FormHelperText color="blue.500" fontSize="xs">
          Saving...
        </FormHelperText>
      )}
      
      {required && !value.trim() && (
        <FormErrorMessage>This question is required</FormErrorMessage>
      )}
    </FormControl>
  );
}
